import { saveAs } from "file-saver";
import { groupBy, sortBy } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { DATE_FORMATS_FNS, showNotification, useCan } from "components";
import { getPinStatusNotificationStatusName } from "components/NotificationsCenter/components/ClaireNotification/components";
import { usePermanentRealTimeTypedQuery } from "components/WebSocket/hooks";
import ENV from "config/Env";
import { permanentBackendQueryKey, typedQueryClient, useTypedQuery } from "hooks/useTypedQuery";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { removeCallForHelpNotification, useCallForHelp, useHandleCallForHelpMutation } from "modules/KeyLockersDashboard/components/KeyLockerCallForHelp/hooks";
import { KeyLockersQueryKeys } from "modules/KeyLockersDashboard/queryKeys";
import { WARRANTY_STORAGE_KEYS } from "modules/WarrantyDashboard/storageKeys";
import { PATHS } from "router/paths";
import { ApiInstance } from "utils/Api";
import { getNameFromURL, getPreference } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { WebSocketMessageListener } from "components";
import type { ExportEvent, KeyLockerCallForHelp, PIN_STATUSES, Pin, User } from "models";

export const userKey = permanentBackendQueryKey<User>({ component: "useUser", baseUrl: ENV.authBaseURL, endpoint: "/session/me" });

export const usePermanentRealTimeUser = () => {
  const { t } = useTranslation();

  useCallForHelp();
  const { mutate: handleCallForHelp } = useHandleCallForHelpMutation();
  const canHandleTheCallForHelp = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.KeylockersRemarks);

  const listeners = useMemo(() => {
    const userListeners: WebSocketMessageListener<User>[] = [
      {
        model: "User",
        action: "upsert",
        callback: message => {
          const user = typedQueryClient.getQueryData(userKey);
          if (!user || user.id !== message.id) return;

          typedQueryClient.setQueryData(userKey, { ...user, ...message.data });
        }
      }
    ];

    const pinListeners: WebSocketMessageListener<Pin>[] = [
      {
        model: "Pin",
        action: "upsert",
        filter: { status_changed: true },
        callback: ({ data: pin, user_id }) => {
          const user = typedQueryClient.getQueryData(userKey);
          const storageSelectedPinStatus = getPreference<PIN_STATUSES[] | null>(WARRANTY_STORAGE_KEYS.PinStatusPreference, null);

          if (!storageSelectedPinStatus || !pin.pin_status_id || !storageSelectedPinStatus.includes(pin.pin_status_id)) return;
          if (!user?.pin_status_notification_enabled || user_id === user.id) return;

          showNotification({
            id: `${uuidv4()} - ${pin.id}`,
            time: formatDate(new Date(), DATE_FORMATS_FNS.hoursMinutes),
            pin,
            type: "PinStatusNotification",
            title: `#${pin.appointment?.wo_nr}`,
            description: `${t("v8_received", "received")} ${getPinStatusNotificationStatusName(pin, t)} ${t("v8_pin_status_update", "pin status update")}`,
            path: `${window.location.origin}${PATHS.APPOINTMENTS}/${pin.appointment_id}`,
            icon: "pin"
          });
        }
      }
    ];

    const exportEventListeners: WebSocketMessageListener<ExportEvent>[] = [
      {
        model: "ExportEvent",
        action: "upsert",
        callback: ({ data: exportEvent }) => {
          showNotification({
            id: `${uuidv4()} - ${exportEvent.id}`,
            time: formatDate(new Date(), DATE_FORMATS_FNS.hoursMinutes),
            type: "ExportNotification",
            title: exportEvent.error ? t("v8_export_failed", "Export failed") : t("v8_your_export_ready", "Your export is ready"),
            description: exportEvent.error || t("v8_export_ready_to_download_content", "Click to download"),
            onNotificationClick: async () => {
              if (Notification.permission !== "granted") {
                const permission = await Notification.requestPermission();
                if (permission !== "granted") return;
              }

              if (exportEvent.url) await saveAs(exportEvent.url, getNameFromURL(exportEvent.url));
              await ApiInstance.post("/users/export/seen", { export_event_id: exportEvent.id }, ENV.systemBaseURL);
            },
            icon: "file export"
          });
        }
      }
    ];

    const keyLockerCallForHelpListeners: WebSocketMessageListener<KeyLockerCallForHelp>[] = [
      {
        model: "KeyLockerCallForHelp",
        action: "upsert",
        callback: ({ data: keyLockerCallForHelpMessage, user_id }) => {
          const user = typedQueryClient.getQueryData(userKey);
          if (user?.id === user_id) return;

          const keyLockerCallForHelp = typedQueryClient.getQueryData(KeyLockersQueryKeys.callForHelp);
          if (!keyLockerCallForHelp) return;

          const callForHelpIdx = keyLockerCallForHelp.findIndex(
            klCallForHelp => klCallForHelp.keylocker_call_for_help_id === keyLockerCallForHelpMessage.keylocker_call_for_help_id
          );

          typedQueryClient.setQueryData(
            KeyLockersQueryKeys.callForHelp,
            callForHelpIdx === -1 ? [...keyLockerCallForHelp, keyLockerCallForHelpMessage] : keyLockerCallForHelp.toSpliced(callForHelpIdx, 1)
          );

          callForHelpIdx === -1
            ? showNotification({
                id: `${uuidv4()} - ${keyLockerCallForHelpMessage.keylocker_call_for_help_id}`,
                time: formatDate(new Date(), DATE_FORMATS_FNS.hoursMinutes),
                type: "KeyLockerCallForHelpNotification",
                title: t("v8_keylocker", "Key Locker"),
                description: t("v8_customer_is_calling_for_help_at_the_key_locker", "Customer is calling for help at the Key Locker"),
                icon: "key",
                onNotificationClick: () => {
                  canHandleTheCallForHelp && handleCallForHelp(keyLockerCallForHelpMessage.keylocker_call_for_help_id);
                }
              })
            : removeCallForHelpNotification(`${keyLockerCallForHelpMessage.keylocker_call_for_help_id}`);
        }
      }
    ];

    return [...userListeners, ...pinListeners, ...exportEventListeners, ...keyLockerCallForHelpListeners];
  }, []);

  const realTimeQuery = usePermanentRealTimeTypedQuery({
    queryKey: userKey,
    queryFn: async ({ baseUrl, endpoint }) => {
      const user: User = (await ApiInstance.post(endpoint, {}, baseUrl)).data;

      user.export_events?.forEach(exportEvent => {
        showNotification({
          id: `${uuidv4()} - ${exportEvent.id}`,
          time: formatDate(new Date(), DATE_FORMATS_FNS.hoursMinutes),
          title: exportEvent.error ? t("v8_export_failed", "Export failed") : t("v8_your_export_ready", "Your export is ready"),
          description: exportEvent.error || t("v8_export_ready_to_download_content", "Click to download"),
          type: "ExportNotification",
          onNotificationClick: async () => {
            if (Notification.permission !== "granted") {
              const permission = await Notification.requestPermission();
              if (permission !== "granted") return;
            }

            if (exportEvent.url) await saveAs(exportEvent.url, getNameFromURL(exportEvent.url));
          },
          icon: "file export"
        });
      });

      return user;
    },
    listeners
  });

  return { user: realTimeQuery.data, isFetched: realTimeQuery.isFetched };
};

const groupACLByResource = (user: User) => (user.acl ? groupBy(sortBy(user.acl, "resource"), "resource") : {});

export const useUserRoleACLs = () => {
  return useTypedQuery({
    queryKey: userKey,
    queryFn: async ({ baseUrl, endpoint }) => (await ApiInstance.post(endpoint, {}, baseUrl)).data,
    select: groupACLByResource
  }).data;
};
